import { supabase } from '../lib/supabase';

export interface Song {
  id: string;
  title: string;
  artist: string;
  genre: string;
  difficulty: string;
  duration: number;
  audio_url: string; // Record track - vocals-only backing track for recording
  cover_url: string;
  lead_vocal_url: string | null;
  catalog_number: string | null;
  full_mix_url?: string | null; // Practice track - full song with vocals for practice mode
  midi_url?: string | null;
  lyrics_url?: string | null;
  release_year?: number;
  popularity?: number;
  language?: string;
  is_deleted?: boolean; // Soft delete flag
  deleted_at?: string | null;
  deleted_by?: string | null;
  is_audition?: boolean; // Flag for audition songs
  hide_from_kiosk?: boolean;
  hide_from_singalong?: boolean;
}

export interface Lyrics {
  id: string;
  song_id: string;
  content: string;
}

export class SongService {
  private songCache: Map<string, Song> = new Map();
  private allSongsCache: Song[] | null = null;
  private lyricsCache: Map<string, string> = new Map();

  /**
   * Get a song by ID
   * Note: This method returns the song even if it's soft deleted,
   * allowing playback of historical recordings. Use getAllSongs() for active songs only.
   */
  async getSong(id: string): Promise<Song | null> {
    if (this.songCache.has(id)) {
      return this.songCache.get(id)!;
    }

    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null;
        }
        throw error;
      }

      if (data) {
        this.songCache.set(id, data);
      }
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all active (non-deleted) songs
   * Deleted songs are filtered out to prevent them from appearing in song lists
   */
  async getAllSongs(): Promise<Song[]> {
    if (this.allSongsCache) {
      return this.allSongsCache;
    }

    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('is_deleted', false)
        .order('title');

      if (error) throw error;

      // Process the URLs to ensure they're public URLs
      const processedSongs = data?.map(song => ({
        ...song,
        cover_url: song.cover_url ? (() => {
          // Extract just the filename from the path
          const cleanPath = song.cover_url.split('/').pop() || '';
          const { data: { publicUrl } } = supabase.storage
            .from('song_covers')
            .getPublicUrl(cleanPath);
          return publicUrl;
        })() : null,
        audio_url: song.audio_url ? (() => {
          const cleanPath = song.audio_url.split('/').pop() || '';
          return supabase.storage
            .from('song_audio')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null,
        lyrics_url: song.lyrics_url ? (() => {
          const cleanPath = song.lyrics_url.split('/').pop() || '';
          return supabase.storage
            .from('song_lyrics')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null
      }));

      if (data) {
        this.allSongsCache = processedSongs;
        processedSongs.forEach(song => this.songCache.set(song.id, song));
      }

      return processedSongs || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all audition songs (where is_audition = true)
   */
  async getAuditionSongs(): Promise<Song[]> {
    try {
      const { data, error } = await supabase
        .from('songs')
        .select('*')
        .eq('is_deleted', false)
        .eq('is_audition', true)
        .order('title');

      if (error) throw error;

      // Process the URLs to ensure they're public URLs
      const processedSongs = data?.map(song => ({
        ...song,
        cover_url: song.cover_url ? (() => {
          const cleanPath = song.cover_url.split('/').pop() || '';
          const { data: { publicUrl } } = supabase.storage
            .from('song_covers')
            .getPublicUrl(cleanPath);
          return publicUrl;
        })() : null,
        audio_url: song.audio_url ? (() => {
          const cleanPath = song.audio_url.split('/').pop() || '';
          return supabase.storage
            .from('song_audio')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null,
        lyrics_url: song.lyrics_url ? (() => {
          const cleanPath = song.lyrics_url.split('/').pop() || '';
          return supabase.storage
            .from('song_lyrics')
            .getPublicUrl(cleanPath).data.publicUrl;
        })() : null
      }));

      return processedSongs || [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get the record track URL (vocals-only backing track for recording)
   */
  async getAudioUrl(songId: string): Promise<string> {
    try {
      const song = await this.getSong(songId);
      if (!song) {
        throw new Error('Song not found');
      }

      if (!song.audio_url) {
        throw new Error('Record track URL not found for song');
      }

      // Remove any existing storage URL prefix to prevent duplication
      const audioPath = song.audio_url.split('/').pop() || '';

      // Get public URL from song_audio bucket
      const { data: { publicUrl }, error } = supabase.storage
        .from('song_audio')
        .getPublicUrl(audioPath);

      if (error) {
        throw new Error(`Failed to get record track URL: ${error.message}`);
      }

      // Verify the URL is accessible
      const response = await fetch(publicUrl, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`Record track file not accessible: ${response.status}`);
      }

      return publicUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get the practice track URL (full song with vocals for practice mode)
   * Falls back to audio_url if full_mix_url is not available
   */
  async getFullMixUrl(songId: string): Promise<string> {
    try {
      const song = await this.getSong(songId);
      if (!song) {
        throw new Error('Song not found');
      }

      // Try to use full_mix_url first, fall back to audio_url for backward compatibility
      const mixUrl = song.full_mix_url || song.audio_url;

      if (!mixUrl) {
        throw new Error('Practice track URL not found for song');
      }

      // If the URL is already a full public URL, just return it
      // This handles URLs that are already properly formatted in the database
      if (mixUrl.startsWith('http://') || mixUrl.startsWith('https://')) {
        return mixUrl;
      }

      // Remove any existing storage URL prefix to prevent duplication
      const audioPath = mixUrl.split('/').pop() || '';

      // Get public URL from song_audio bucket
      const { data: { publicUrl }, error } = supabase.storage
        .from('song_audio')
        .getPublicUrl(audioPath);

      if (error) {
        throw new Error(`Failed to get practice track URL: ${error.message}`);
      }

      return publicUrl;
    } catch (error) {
      throw error;
    }
  }

  async getLyricsUrl(songId: string): Promise<string | null> {
    try {
      const song = await this.getSong(songId);
      if (!song?.lyrics_url) {
        return null;
      }

      // Remove any existing storage URL prefix to prevent duplication
      const lyricsPath = song.lyrics_url.split('/').pop() || '';
      
      // Get public URL from song_lyrics bucket
      const { data: { publicUrl }, error } = supabase.storage
        .from('song_lyrics')
        .getPublicUrl(lyricsPath);

      if (error) {
        throw new Error(`Failed to get lyrics URL: ${error.message}`);
      }
      
      return publicUrl;
    } catch (error) {
      return null;
    }
  }

  async getLyrics(songId: string): Promise<string> {
    if (this.lyricsCache.has(songId)) {
      return this.lyricsCache.get(songId)!;
    }

    try {
      // Get lyrics URL
      const lyricsUrl = await this.getLyricsUrl(songId);
      if (!lyricsUrl) {
        return this.getFallbackLyrics();
      }

      // Verify the URL is accessible
      const response = await fetch(lyricsUrl, { method: 'HEAD' });
      if (!response.ok) {
        return this.getFallbackLyrics();
      }

      // Fetch lyrics content
      const lyricsResponse = await fetch(lyricsUrl);
      if (!lyricsResponse.ok) {
        return this.getFallbackLyrics();
      }

      const lyrics = await lyricsResponse.text();
      if (!lyrics.trim()) {
        return this.getFallbackLyrics();
      }

      this.lyricsCache.set(songId, lyrics);
      return lyrics;

    } catch (error) {
      return this.getFallbackLyrics();
    }
  }

  private getFallbackLyrics(): string {
    return `[00:00.00]No lyrics available for this song
[00:05.00]Try singing along anyway!`;
  }
}